import { skipToken } from '@reduxjs/toolkit/query';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useGetCareOrdersQuery, useGetWorkingOrdersQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import type { CareOrder } from '@talos/kyoko';
import { useMemo } from 'react';

interface UseCareOrWorkingOrderParams {
  orderID: string | undefined;
  tag: string;
}

interface CareOrWorkingOrderResult {
  /** Parent order from either the care stream or, when netting is enabled, the working-order stream. */
  careOrder: CareOrder | undefined;
  isLoading: boolean;
}

/** If you just have a Care/WorkingOrderID, and you don't know if its a care order or a working order, you just want to get whichever one it is, this hook is for you */
export function useCareOrWorkingOrder({ orderID, tag }: UseCareOrWorkingOrderParams): CareOrWorkingOrderResult {
  const { enableCareOrderNetting } = useFeatureFlag();
  const careOrderRequest = orderID ? { OrderID: orderID, tag } : skipToken;
  const workingOrderRequest = orderID && enableCareOrderNetting ? { OrderID: orderID, tag } : skipToken;
  const careOrdersQuery = useGetCareOrdersQuery(careOrderRequest);
  const workingOrdersQuery = useGetWorkingOrdersQuery(workingOrderRequest);

  return useMemo(
    () => ({
      careOrder: orderID ? (careOrdersQuery.data?.get(orderID) ?? workingOrdersQuery.data?.get(orderID)) : undefined,
      isLoading: careOrdersQuery.isLoading || workingOrdersQuery.isLoading,
    }),
    [careOrdersQuery.data, careOrdersQuery.isLoading, orderID, workingOrdersQuery.data, workingOrdersQuery.isLoading]
  );
}
